import React, { useState, useEffect }  from 'react';
import styled from 'styled-components';
import { browserHistory } from 'react-router';
import { RegistrationGrid } from '@parchment/forms';
import { ORDER_TYPE } from '../common/constants/OrderType';
import useLearner from '../common/hooks/LearnerHooks';
import useTPOUser from '../common/hooks/TPOUserHooks';
import useMember from '../common/hooks/MemberHooks';
import useMemberAdmin from '../common/hooks/MemberAdminHooks';
import useEmailVerification from '../common/hooks/EmailVerificationHooks';
import useAuthenticateUser from '../common/hooks/AuthenticateUserHooks';
import {Banner, curiousBlue, emperor, FormContainer, white} from '@parchment/atoms';
import MemberMessages from './MemberMessages';
import { MessageContext, MessagePageLocation, MessageUserRole, MessageBannerContext } from '../common/constants/MessageConstants';
import { WarningIcon } from "@parchment/icons";
const noop = () => {};

export default function RegistrationView ({
    email = '',
    member = {},
    ssoInfo = {},
    resetEmail = noop,
    orderType = ORDER_TYPE.LEARNER,
    submitting = false,
    registrationTokenRecord = {},
    memberServiceMessages = {}
}) {
    const { address = {} } = member;
    const [ formData, setFormData ] = useState({
        firstName: ssoInfo?.firstName || '',
        middleName: ssoInfo?.middleName || '',
        lastName: ssoInfo?.lastName || '',
        organizationName: '',
        organizationType: '',
        title: '',
        dateOfBirth: ssoInfo?.dateOfBirth || '',
        highestLevelOfEducation: ssoInfo?.highestLevelOfEducation || '',
        phone: ssoInfo?.phone || '',
        address: ssoInfo?.address || {
            country: address?.country ||  '',
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip: ''
        },
        password: ''
    });
    const [ formStatus, setFormStatus ] = useState({
        showErrorBanner: false,
        errorBannerMessage: '',
        submitting: submitting
    });
    const { submitting: formSubmitting } = formStatus;
    const { learner, createLearner, status: learnerStatus } = useLearner();
    const { tpoUser, createTPOUser, status: tpoUserStatus } = useTPOUser();
    const { member: newMember, createMember, status: memberStatus } = useMember();
    const { memberAdmin, createMemberAdmin, status: memberAdminStatus } = useMemberAdmin();
    const { verificationCode, requestVerification, status: emailVerificationStatus } = useEmailVerification();
    const { authenticationInfo, loginUser, status: authenticationStatus } = useAuthenticateUser();

    useEffect(() => {
        if (formSubmitting && orderType === ORDER_TYPE.THIRD_PARTY) {
            const userRequest = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: email,
                phoneNumber: formData.phone,
                title: formData.title,
                password: formData.password,
                secureRoles: ['ReceiverDocumentPrimary', 'SiteAdminReceiver']
            };
            createTPOUser(userRequest);
        } else if (formSubmitting && orderType === ORDER_TYPE.LEARNER) {
            const learnerRequest = Object.assign({}, formData, {
                email: email,
                memberId: member.id,
                memberName: member.name,
                memberType: member.type,
                csrfToken: window.csrfToken
            });
            createLearner(learnerRequest);
        }
    }, [formSubmitting]);

    useEffect(() => {
        if (formSubmitting && learner) {
            loginUser(email, formData.password, member.id, orderType);
        }
    }, [learner]);

    useEffect(() => {
        if (formSubmitting && tpoUser) {
            const memberRequest = {
                name: formData.organizationName,
                receiver: true,
                approvalMethod: 20,
                customerType: 'ST',
                deliveryMethod: 'electronic',
                pendingVerify: 'T',
                memberType: 'CO',
                riType: formData.organizationType,
                paperStatus: 'none',
                openDestination: true,
                receiverStatus: 'TPO',
                address: {
                    address1: formData.address.address1,
                    address2: formData.address.address2,
                    city: formData.address.city,
                    zoneName: formData.address.state,
                    countryName: formData.address.country,
                    postCode: formData.address.zip
                },
                phone: formData.phone,
                emailAddress: email
            };
            createMember(memberRequest);
        }
    }, [tpoUser]);

    useEffect(() => {
        if (formSubmitting && tpoUser && newMember) {
            const { id: individualId, title } = tpoUser;
            const { id: memberId } = newMember;

            const memberAdminRequest = {
                individualId: individualId,
                membrfk: memberId,
                isPrimary: 'Y',
                title: title,
                membrType: 'RIADMIN'
            };
            createMemberAdmin(memberAdminRequest);
        }
    }, [newMember]);

    useEffect(() => {
        if (formSubmitting && memberAdmin) {
            const { individualEmailId } = tpoUser;
            requestVerification(individualEmailId);
        }
    }, [memberAdmin]);

    useEffect(() => {
        if (formSubmitting && verificationCode.sentEmail) {
            loginUser(email, formData.password, member.id);
        }
    }, [verificationCode]);

    useEffect(() => {
        if (formSubmitting && authenticationInfo.authenticated) {
            setFormStatus((prevState) => ({
                ...prevState,
                submitting: false
            }));
            if (authenticationInfo.loggedInFromSso && !authenticationInfo.needsVerified && orderType === ORDER_TYPE.LEARNER) {
                window.location.href = `/u/auth/login?memberId=${member.id}`;
            } else {
                browserHistory.push( `/u/registration/${member.id}/institution/confirm`);
            }
        }
    }, [authenticationInfo]);

    useEffect(() => {
        const errorStatus = [learnerStatus, tpoUserStatus, memberStatus,
            memberAdminStatus, emailVerificationStatus, authenticationStatus].find(status => status && status.hasError);
        if (formSubmitting && errorStatus) {
            setFormStatus({
                showErrorBanner: true,
                errorBannerMessage: errorStatus.errorMessage,
                submitting: false
            });
        }
    }, [learnerStatus, tpoUserStatus, memberStatus, memberAdminStatus, emailVerificationStatus, authenticationStatus]);

    useEffect(() => {
        if(registrationTokenRecord?.id) {
            setFormData((prevState) => ({
                ...prevState,
                firstName: registrationTokenRecord.studentFirstName,
                lastName: registrationTokenRecord.studentLastName,
                dateOfBirth: registrationTokenRecord.studentDOB
            }));
        }
    }, [registrationTokenRecord]);

    const handleSubmit = () => {
        setFormStatus((prevState) => ({
            ...prevState,
            submitting: true
        }));
    };
    const messagesAvailable = memberServiceMessages && (memberServiceMessages?.globalMessages?.length > 0 || memberServiceMessages?.associatedMemberMessages?.length > 0 || memberServiceMessages?.memberMessages?.length > 0);

    const renderRegistrationSimpleMessage = () => {
        return (
            <MemberMessages
                memberServiceMessages={memberServiceMessages}
                pageLocation={MessagePageLocation.REGISTRATION_REGISTER}
                userRole={(orderType === ORDER_TYPE.LEARNER) ? MessageUserRole.LEARNER : MessageUserRole.THIRD_PARTY}
                messageContexts={[MessageContext.SIMPLE]}
            />
        );
    };

    const renderDateOfBirthErrorBannerMessage = () => {
        return (
            <MessageBanner
                icon={<WarningIcon />}
                title={ 'DATE OF BIRTH VALIDATION ERROR'}
                message={'You must be at least 13 years of age to use this service'}
                action={ null }
                actionLink={ null }
                context={ MessageBannerContext.WARNING }
            />
        )
    }

    const isAboveThirteenYearsOld = (inputDate, currentDate = new Date()) => {

        if (inputDate === null) return false;

        const dateParts = inputDate.split('-');

        if (dateParts.length !== 3) return false;

        const inputMonth = parseInt(dateParts[1]) - 1; // Months are 0-indexed in JS Date
        const inputDay = parseInt(dateParts[2]);
        const inputYear = parseInt(dateParts[0]);

        const inputDateObj = new Date(inputYear, inputMonth, inputDay);

        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();
        const currentDay = currentDate.getDate();

        const thirteenYearsAgo = new Date(currentYear - 13, currentMonth, currentDay);
        return inputDateObj <= thirteenYearsAgo;
    }

    const handleRegistrationGridSubmit = (updatedData, fieldName) => {
        if(orderType === ORDER_TYPE.THIRD_PARTY || (updatedData.dateOfBirth !== '' && isAboveThirteenYearsOld(updatedData.dateOfBirth))) {
            setFormStatus((prevState) => ({
                ...prevState,
                submitting: true
            }));
        } else {
            setFormStatus((prevState) => ({
                ...prevState,
                dateOfBirth: ''
            }));
        }
    }

    return (
        <FormContainer context='recessed' className='registration-section'>
            {
                formStatus.showErrorBanner && <FormContainer.Prefix className='error-banner-container'>
                    <Banner visible={formStatus.showErrorBanner} title='error' context='error' dismissable={true}
                            size='large' message={formStatus.errorBannerMessage}
                            onDismiss={() => {
                                setFormStatus((prevState) => ({
                                    ...prevState,
                                    showErrorBanner: false,
                                    errorBannerMessage: ''
                                }));
                            }} />
                </FormContainer.Prefix>
            }
            {
                messagesAvailable && renderRegistrationSimpleMessage()
            }
            { (formData.dateOfBirth !== null && formData.dateOfBirth !== '' && !isAboveThirteenYearsOld(formData.dateOfBirth)) && renderDateOfBirthErrorBannerMessage() }
            <RegistrationWrapper>
                <RegistrationGrid formData={formData}
                                  email={email}
                                  registrationType={orderType}
                                  loading={formSubmitting}
                                  onChange={(updatedData) => {
                                      setFormData( updatedData );
                                  }}
                                  onSubmit={ handleRegistrationGridSubmit }
                                  onEmailChange={() => resetEmail()} />
            </RegistrationWrapper>
        </FormContainer>
    );
}

const getBannerContextColor = (context) => {
    switch (context) {
        case MessageBannerContext.WARNING:
            return white;
        case MessageBannerContext.LIGHT:
            return emperor;
        case MessageBannerContext.DARK:
        default:
            return curiousBlue;
    }
};

const MessageBanner = styled(Banner)`
  a {
    color: ${props => getBannerContextColor(props.context)} !important;
    &:hover {
      opacity: 0.8
    }
  }
`;

const RegistrationWrapper = styled.div`
    #RegistrationGrid .personal-information h6 {
        margin: 0;
    }
`;
RegistrationWrapper.displayName = 'RegistrationWrapper';
